import {ChangeDetectionStrategy, Component, input} from '@angular/core';
import {RouterLink} from '@angular/router';
import {ThaiDateTimePipe} from '../../../shared/commons/thai-datetime.pipe';
import {DEFAULT_TRADE_UNIT, TRADE_BASE, tradeKindBadgeClass, tradeKindLabel, tradeRecordLabel} from '../trade.constants';
import {formatQuantityWithUnit} from '../trade-utils';
import {ChainTreeNode, ChainTreePins} from './trade-chain-tree';
import {JOURNEY_COLORS, JOURNEY_CURRENT_COLOR} from './trade-journey-sankey';

/**
 * One level of the journey tree; it renders itself again for each row's children, so an origin lot
 * opens into its buyers and each buyer into theirs, as deep as the journey goes.
 */
@Component({
  selector: 'app-trade-chain-tree',
  standalone: true,
  imports: [RouterLink, ThaiDateTimePipe],
  template: `
    <ul class="chain-tree" [class.is-nested]="depth() > 0">
      @for (node of nodes(); track node.record.id) {
        <li class="chain-tree-node" [class.is-current]="node.current">
          <div class="chain-tree-row">
            @if (pinOf(node.record.id); as pin) {
              <span class="chain-tree-pin" [class.is-current]="node.current"
                    [style.background]="node.current ? currentColor : colors[node.record.kind ?? 'HARVEST']"
                    [style.color]="node.current || node.record.kind === 'TRANSFORM' ? '#fff' : '#04532b'"
                    [title]="'หมุดที่ ' + pin + ' บนแผนที่'">{{ pin }}</span>
            } @else {
              <span class="chain-tree-dot" aria-hidden="true"></span>
            }
            <div class="chain-tree-body">
              <div class="d-flex flex-wrap align-items-center gap-2">
                <span [class]="'badge ' + tradeKindBadgeClass(node.record.kind)">{{ tradeRecordLabel(node.record) }}</span>
                @if (node.record.id != null && !node.current) {
                  <a class="fw-semibold chain-tree-link" [routerLink]="[tradeBase, node.record.id]">{{ node.record.businessTitle || '-' }}</a>
                } @else {
                  <span class="fw-semibold">{{ node.record.businessTitle || '-' }}</span>
                }
                @if (node.current) {
                  <span class="badge text-bg-dark">รายการนี้</span>
                }
                @if (node.record.lotCode) {
                  <span class="badge text-bg-light">ล็อต {{ node.record.lotCode }}</span>
                }
              </div>
              <div class="small text-secondary">
                @if (node.quantity != null) {
                  รับไป {{ formatQuantityWithUnit(node.quantity, unit) }}
                } @else if (node.record.quantity != null) {
                  {{ formatQuantityWithUnit(node.record.quantity, node.record.unit || unit) }}
                }
                @if (node.record.productName) {
                  · {{ node.record.productName }}
                }
                @if (node.record.recordedAt) {
                  · {{ node.record.recordedAt | thaidatetime }}
                }
              </div>
            </div>
          </div>
          @if (node.checkpointName) {
            <ul class="chain-tree is-nested">
              <li class="chain-tree-node is-end">
                <div class="chain-tree-row">
                  @if (checkpointPinOf(node.record.id); as pin) {
                    <span class="chain-tree-pin" [style.background]="colors['CHECKPOINT']" style="color: #fff"
                          [title]="'หมุดที่ ' + pin + ' บนแผนที่'">{{ pin }}</span>
                  } @else {
                    <span class="chain-tree-dot" aria-hidden="true"></span>
                  }
                  <div class="chain-tree-body">
                    <div class="d-flex flex-wrap align-items-center gap-2">
                      <span class="badge text-bg-danger">ด่านที่ส่งออก</span>
                      <span class="fw-semibold">{{ node.checkpointName }}</span>
                    </div>
                    <div class="small text-secondary">สิ้นสุดห่วงโซ่ (ส่งออก)</div>
                  </div>
                </div>
              </li>
            </ul>
          }
          @if (node.children.length > 0) {
            <app-trade-chain-tree [nodes]="node.children" [pins]="pins()" [depth]="depth() + 1"></app-trade-chain-tree>
          }
        </li>
      }
    </ul>
  `,
  styles: [`
    :host { display: block; }

    .chain-tree {
      margin: 0;
      padding: 0;
      list-style: none;
    }

    /* Nested levels hang off a guide line under their parent row. */
    .chain-tree.is-nested {
      margin-left: 0.28rem;
      padding-left: 1rem;
      border-left: 2px solid #e2e8f0;
    }

    .chain-tree-node {
      position: relative;
      padding: 0.15rem 0 0.35rem;
      font-size: 0.71rem;
    }

    .chain-tree-row {
      position: relative;
      display: flex;
      gap: 0.55rem;
      align-items: flex-start;
    }

    .chain-tree-dot {
      flex: 0 0 auto;
      width: 0.6rem;
      height: 0.6rem;
      margin-top: 0.3rem;
      border-radius: 50%;
      background: #cbd5e1;
    }

    .is-end .chain-tree-dot { background: #e11d48; }

    /* The map's pin number, in the map's colour, so the two views read as one. */
    .chain-tree-pin {
      flex: 0 0 auto;
      display: inline-grid;
      place-items: center;
      min-width: 1.15rem;
      height: 1.15rem;
      padding: 0 0.25rem;
      margin-top: 0.05rem;
      border: 1.5px solid #fff;
      border-radius: 999px;
      font-size: 0.66rem;
      font-weight: 700;
      line-height: 1;
      box-shadow: 0 1px 3px rgb(15 23 42 / 30%);
    }

    .chain-tree-pin.is-current {
      min-width: 1.3rem;
      height: 1.3rem;
      box-shadow: 0 0 0 3px rgba(4, 83, 43, 0.2);
    }

    /* The record being viewed stands out in its place on the journey. */
    .is-current > .chain-tree-row .chain-tree-dot {
      background: #0f172a;
      box-shadow: 0 0 0 3px rgba(15, 23, 42, 0.15);
    }

    .is-current > .chain-tree-row .chain-tree-body {
      padding: 0.4rem 0.6rem;
      border: 1px solid #e2e8f0;
      border-radius: 0.5rem;
      background: #f8fafc;
      color: #0f172a;
    }

    .is-current > .chain-tree-row .fw-semibold { font-weight: 700 !important; }

    .chain-tree-body {
      min-width: 0;
      overflow-wrap: anywhere;
    }

    .chain-tree-body .small,
    .chain-tree-body .text-secondary { font-size: 0.68rem; }

    .chain-tree-link {
      color: var(--primary-accent, #04532b);
      text-decoration: none;
    }

    .chain-tree-link:hover {
      color: #033d20;
      text-decoration: underline;
    }
  `],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class TradeChainTreeComponent {
  readonly nodes = input.required<ChainTreeNode[]>();
  /** Map pin numbers by record id; a record without one gets a plain dot. */
  readonly pins = input<ChainTreePins>({records: new Map(), checkpoints: new Map()});
  readonly depth = input(0);

  protected readonly colors = JOURNEY_COLORS;
  protected readonly currentColor = JOURNEY_CURRENT_COLOR;

  protected pinOf(id: number | null | undefined): number | undefined {
    return id == null ? undefined : this.pins().records.get(id);
  }

  protected checkpointPinOf(id: number | null | undefined): number | undefined {
    return id == null ? undefined : this.pins().checkpoints.get(id);
  }

  protected readonly unit = DEFAULT_TRADE_UNIT;
  protected readonly tradeBase = TRADE_BASE;
  protected readonly tradeKindLabel = tradeKindLabel;
  protected readonly tradeRecordLabel = tradeRecordLabel;
  protected readonly tradeKindBadgeClass = tradeKindBadgeClass;
  protected readonly formatQuantityWithUnit = formatQuantityWithUnit;
}
